/**
 * Facebook Conversion API Integration
 * Sends events via backend proxy to keep the access token server-side.
 */

import { fetcher } from '#lib/backendFetcher';

/**
 * Generates a unique event-id for deduplication between browser and CAPI
 * @returns {string} UUID v4 Event-ID
 */
export function generateEventId() {
	return crypto.randomUUID();
}

function getCookie(name) {
	const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
	return match ? decodeURIComponent(match[1]) : null;
}

function getFbc() {
	const cookieValue = getCookie('_fbc');
	if (cookieValue) return cookieValue;

	const fbclid = new URLSearchParams(window.location.search).get('fbclid');
	if (!fbclid) return null;

	return `fb.1.${Date.now()}.${fbclid}`;
}

function getFbp() {
	return getCookie('_fbp');
}

/**
 * Sends a CompleteRegistration event to the Facebook Conversion API via the backend.
 * @param {Object} userData - User data from the registration
 * @param {string} userData.email - Email address of the user
 * @param {string} userData.firstName - First name of the user
 * @param {string} userData.lastName - Last name of the user
 * @param {string} [userData.externalId] - Stable auth user id, hashed server-side as external_id
 * @param {string} [eventId] - Event ID for deduplication with browser event
 * @returns {Promise<void>}
 */
async function trackCompleteRegistration(userData, eventId = null) {
	try {
		if (!userData?.email || !userData?.firstName || !userData?.lastName) {
			return;
		}

		const dedupEventId = eventId || generateEventId();

		const fbc = getFbc();
		const fbp = getFbp();

		await fetcher('/api/v1/platform/tracking/facebook-conversion', {
			method: 'POST',
			body: {
				email: userData.email,
				firstName: userData.firstName,
				lastName: userData.lastName,
				eventId: dedupEventId,
				sourceUrl: window.location.href,
				userAgent: navigator.userAgent,
				...(fbc && { fbc }),
				...(fbp && { fbp }),
				...(userData.externalId && { externalId: userData.externalId }),
			},
		});
	} catch {
		// ignored
	}
}

/**
 * Sends a Lead event (signup step 1 — email captured) to the Facebook Conversion API via the
 * backend. Mirrors the browser Pixel's Lead event via the shared event id so Meta deduplicates
 * the two. At this point only the email is available as a match key (no user id yet).
 * @param {string} email - Email address entered in step 1
 * @param {string} eventId - Event ID shared with the browser Pixel event for deduplication
 * @returns {Promise<void>}
 */
export async function trackLead(email, eventId) {
	try {
		if (!email) return;

		const fbc = getFbc();
		const fbp = getFbp();

		await fetcher('/api/v1/platform/tracking/facebook-lead', {
			method: 'POST',
			body: {
				email,
				eventId,
				sourceUrl: window.location.href,
				userAgent: navigator.userAgent,
				...(fbc && { fbc }),
				...(fbp && { fbp }),
			},
		});
	} catch {
		// ignored
	}
}

export default trackCompleteRegistration;
